/**
 * clipboard.ts — Platform-aware clipboard utility.
 *
 * Uses @capacitor/clipboard on native for reliable access (iOS permission
 * prompts, background paste). Falls back to navigator.clipboard on web.
 *
 * Pattern matches haptics.ts — no-ops silently on failure.
 */

import { Capacitor } from "@capacitor/core"

/**
 * Write text to the system clipboard.
 * Uses Capacitor plugin on native, Web Clipboard API on web.
 */
export async function writeClipboard(text: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Clipboard } = await import("@capacitor/clipboard")
      await Clipboard.write({ string: text })
    } catch {
      // Fallback to web API if plugin fails
      await navigator.clipboard.writeText(text)
    }
  } else {
    await navigator.clipboard.writeText(text)
  }
}

/**
 * Read text from the system clipboard.
 * Uses Capacitor plugin on native, Web Clipboard API on web.
 *
 * Returns empty string if read fails or clipboard is empty.
 */
export async function readClipboard(): Promise<string> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Clipboard } = await import("@capacitor/clipboard")
      const result = await Clipboard.read()
      return result.value ?? ""
    } catch {
      // Fallback to web API
      try {
        return await navigator.clipboard.readText()
      } catch {
        return ""
      }
    }
  } else {
    try {
      return await navigator.clipboard.readText()
    } catch {
      return ""
    }
  }
}
